import { Dispatch, MutableRefObject, SetStateAction, useEffect } from "react"
import { SoundName } from "@/hooks/useAudioController"
import { ICard } from "@/store/apis/deckAPI"
import {
  IGameplayEvent,
  IGameplayStatePlayer,
  IGameplayStateResponse
} from "@/store/apis/gameplayAPI"
import { AppDispatch } from "@/store/index"
import {
  setEventFulfilled,
  setEvents,
  setPrevState
} from "@/store/slices/gameplay"
import { ICardItem, makeCardItem, mockCardData, mockCardGoldData } from ".."
import { IUseCardItemsReturn } from "./useCardItems"

interface IUseCardEventsParams {
  events: IGameplayEvent[]
  isEventsFulfilled: boolean
  gameState: IGameplayStateResponse | undefined
  gamePrevState: IGameplayStateResponse | undefined
  player: IGameplayStatePlayer | null | undefined
  opponent: IGameplayStatePlayer | null | undefined
  sessionId: string | undefined
  isMe: (playerIdentic?: string, currentPlayerId?: string) => boolean

  isCardAnimation: boolean
  refItems: MutableRefObject<ICardItem[]>
  refUserSelectCardInAnimation: MutableRefObject<boolean>
  setItems: Dispatch<SetStateAction<ICardItem[]>>
  withFlip: (fn: () => void) => void
  moveTo: IUseCardItemsReturn["moveTo"]
  addTo: IUseCardItemsReturn["addTo"]

  opponentCards: ICardItem[]
  goldCards: ICardItem[]

  dispatch: AppDispatch
  playSound: (name: SoundName) => Promise<void>
  getDeckLastCard: (sessionId: string) => { unwrap: () => Promise<ICard> }
}

export const useCardEvents = ({
  events,
  isEventsFulfilled,
  gameState,
  gamePrevState,
  player,
  opponent,
  sessionId,
  isMe,
  isCardAnimation,
  refItems,
  refUserSelectCardInAnimation,
  setItems,
  withFlip,
  moveTo,
  addTo,
  opponentCards,
  goldCards,
  dispatch,
  playSound,
  getDeckLastCard
}: IUseCardEventsParams): void => {
  const checkEvents = async () => {
    if (!isEventsFulfilled || isCardAnimation || !gameState) return

    dispatch(setEventFulfilled(false))
    dispatch(setEvents([]))
    dispatch(setPrevState(gameState))

    if (events.find((event) => isMe(event.to))) {
      refUserSelectCardInAnimation.current = true
    } else {
      refUserSelectCardInAnimation.current = false
    }

    // Local pool instead of stale refItems.current
    const availableDeckCardIds = refItems.current
      .filter((i) => i.owner === "deck")
      .map((i) => i.id)

    if (gamePrevState?.deckSize === 0 && gameState.deckSize > 0) {
      playSound("deck-reshuffled")

      const count = gameState.deckSize + 1
      const newDeckItems = Array.from({ length: count }).map(() =>
        makeCardItem({ owner: "deck", order: 0, data: mockCardData })
      )

      availableDeckCardIds.push(...newDeckItems.map((i) => i.id))

      withFlip(() => {
        setItems((prev) => {
          const maxOrder = prev
            .filter((i) => i.owner === "deck")
            .reduce((max, i) => Math.max(max, i.order), 0)
          const n = maxOrder + 1

          return [
            ...prev,
            ...newDeckItems.map((item) => ({ ...item, order: n }))
          ]
        })
      })

      // Yield to React to process setItems before Flip captures new DOM state
      await new Promise((resolve) => setTimeout(resolve, 10))
    }

    const isDeckToUser = events.some(
      (event) => event.from === "DECK" && isMe(event.to)
    )

    let lastCard: ICard | null = null

    if (isDeckToUser) {
      lastCard = await getDeckLastCard(sessionId ?? "").unwrap()
    }

    for (const event of events) {
      switch (event.from) {
        case "DECK": {
          const deckCardId = availableDeckCardIds.pop()

          if (!deckCardId) break

          playSound("deck-to-player")

          if (isMe(event.to)) {
            moveTo(deckCardId, "user", "deck-user", {
              ...lastCard,
              ownerId: event.to
            })
          } else {
            moveTo(deckCardId, "opponent", "deck-opponent", {
              ownerId: event.to
            })
          }
          break
        }
        case "TABLE": {
          break
        }
        case player?.player: {
          if (event.card?.tier === "gold") {
            addTo("gold", mockCardGoldData, 1)
            moveTo(goldCards[0]?.id, "active", "gold-active", {
              ...event.card,
              ownerId: event.from
            })
            break
          }

          break
        }
        case opponent?.player:
          switch (event.to) {
            case "TABLE":
              moveTo(opponentCards[0]?.id, "active", "opponent-active", {
                ...event.card,
                ownerId: event.from
              })

              if (event.card?.tier === "gold") {
                addTo("opponent", {
                  ...mockCardData,
                  ownerId: opponent?.player
                })
              }

              break
            case "GRAVEYARD":
              moveTo(
                opponentCards[0]?.id,
                "removed-opponent",
                "opponent-removed"
              )
              break

            default:
              break
          }
          break
      }
    }
  }

  useEffect(() => {
    checkEvents()
  }, [isEventsFulfilled, events, isCardAnimation])
}
